import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BrandingService } from '../../../../../../branding/branding.service';
import { SsoAccountFacade } from '../../../../../sso-account/domain/+state/sso-account.facade';
import { IdentityVerificationFacade } from '../../../../domain/+state/identity-verification.facade';
import { CompanyColorSchemaEnum } from '../../../../../../../lib/branding/branding.model';
import { DeviceDetectionService } from '../../../../../../../lib/device/device-detection.service';
import { SessionStorageKey, SessionStorageState } from '../../../../../../../lib/helpers/session-storage.helper';
import { IdVerseContentConfig, IdVerseViewData } from '../models/id-verse-content.model';
import { IdVerseFlowHandlerService } from './id-verse-flow-handler.service';
import { IdVerseContentStateService } from './id-verse-content-state.service';
import { getIdVerseLogoUrl, getQrLogoUrl, getMobileDownloadUrl } from '../models/id-verse-content.data';

@Injectable()
export class IdVerseContentFacade {

  constructor(
    private readonly brandingService: BrandingService,
    private readonly ssoAccountFacade: SsoAccountFacade,
    private readonly identityVerificationFacade: IdentityVerificationFacade,
    private readonly deviceDetectionService: DeviceDetectionService,
    private readonly flowHandler: IdVerseFlowHandlerService,
    private readonly stateService: IdVerseContentStateService,
    private readonly router: Router
  ) { }

  getComponentData$(): Observable<IdVerseViewData> {
    return combineLatest([
      this.stateService.getState$(),
      this.brandingService.getBranding$(),
      this.deviceDetectionService.getDeviceData$(),
      this.ssoAccountFacade.getCurrentSsoAccount$(),
    ]).pipe(
      map(([state, branding, deviceData, ssoAccount]) => {
        const colorSchema = branding?.colorSchema as CompanyColorSchemaEnum;
        return {
          state,
          logoUrl: getIdVerseLogoUrl(colorSchema),
          qrLogoUrl: getQrLogoUrl(colorSchema),
          appStoreUrl: branding?.appStoreUrl,
          playStoreUrl: branding?.playStoreUrl,
          mobileDownloadUrl: getMobileDownloadUrl(branding?.portalUrl),
          deviceData,
          currentSsoAccount: ssoAccount,
          customerName: `${ssoAccount?.firstName || ''} ${ssoAccount?.lastName || ''}`.trim(),
        } as IdVerseViewData;
      })
    );
  }

  init(config: IdVerseContentConfig): void {
    this.flowHandler.loadInitialData(config);
  }

  clear(): void {
    this.flowHandler.stopPolling();
    this.stateService.clearState();
  }

  onClickPendingContinue(data: IdVerseViewData, config: IdVerseContentConfig): void {
    this.flowHandler.handlePendingContinue(
      data.currentSsoAccount?.id,
      data.state.verificationUrl,
      data.deviceData,
      config,
    );
  }

  onClickTryAgain(data: IdVerseViewData): void {
    this.flowHandler.onTryAgain(data.currentSsoAccount.id, data.deviceData);
  }

  onClickSwitchToKba(loanId: string): void {
    this.identityVerificationFacade.confirmSwitchToKba()
      .pipe(switchMap(() => this.ssoAccountFacade.refreshSsoAccountCheckoutState()))
      .subscribe(() => {
        this.router.navigateByUrl(loanId ? `/account/${loanId}/setup` : '/account/setup');
      });
  }

  onClickContinueInBrowser(): void {
    this.flowHandler.onContinueInBrowser();
  }

  restoreFlowAfterExternalReturn(config: IdVerseContentConfig): void {
    const hasReturnPendingMarker: boolean = SessionStorageState.get(SessionStorageKey.IdVerseReturnPending) === true;

    if (!hasReturnPendingMarker) {
      return;
    }

    SessionStorageState.remove(SessionStorageKey.IdVerseReturnPending);
    this.flowHandler.reloadInitialData(config);
  }

  protected navigateTo(url: string): void {
    window.location.assign(url);
  }
}
